import { guideVideo } from "./guide-video";
import { routeSrcset } from "./resized-images";

// The runtime half of stories (see rich-text.ts): their grammar, reading, plain text and HTML,
// with no schema library, so a creator site's script stays small. The API's schema reads stories
// with this same reader, so what a page draws and what the API accepts never differ.

export const textAlignments = ["left", "center", "right", "justify"] as const;
const nodeTypes = [
  "doc",
  "paragraph",
  "heading",
  "text",
  "hardBreak",
  "bulletList",
  "orderedList",
  "listItem",
  "blockquote",
  "horizontalRule",
  "codeBlock",
  "gallery",
  "videoEmbed",
  "image",
  "table",
  "tableRow",
  "tableCell",
  "tableHeader",
] as const;
const plainMarks = ["bold", "italic", "strike", "code", "underline"] as const;
export type RichTextMark =
  | { type: (typeof plainMarks)[number] }
  | { type: "link"; attrs: { href: string } };
export type RichTextNode = {
  type: (typeof nodeTypes)[number];
  text?: string;
  attrs?: {
    level?: number;
    start?: number;
    language?: string | null;
    textAlign?: (typeof textAlignments)[number] | null;
    videoUrl?: string;
    mediaId?: string;
    alt?: string;
    title?: string | null;
    colspan?: number;
    rowspan?: number;
    colwidth?: number[] | null;
  };
  marks?: RichTextMark[];
  content?: RichTextNode[];
};

export function safeTextLink(value: string) {
  if (/\s|[<>]/.test(value)) return false;
  try {
    const url = new URL(value);
    return ["http:", "https:", "mailto:"].includes(url.protocol) && !url.username && !url.password;
  } catch {
    return false;
  }
}

const unsupported = "Unsupported story structure.";
const linkProblem = "Use an https, http, or mailto link.";
const videoProblem = "Use a YouTube or Vimeo video link.";
class StoryProblem extends Error {}
const fail = (message = unsupported): never => {
  throw new StoryProblem(message);
};
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isWhole = (value: unknown, min: number, max: number) =>
  Number.isInteger(value) && (value as number) >= min && (value as number) <= max;
const isText = (value: unknown, max: number): value is string =>
  typeof value === "string" && value.length <= max;

// Bound the tree before reading it; content is untrusted API input.
function boundsProblem(value: unknown) {
  const queue: { value: unknown; depth: number }[] = [{ value, depth: 0 }];
  let count = 0,
    size = 0;
  while (queue.length) {
    const current = queue.pop()!;
    if (++count > 10000 || current.depth > 16) return "This story is too complex.";
    if (!current.value || typeof current.value !== "object") continue;
    const item = current.value as Record<string, unknown>;
    if (typeof item.text === "string") size += item.text.length;
    const attrs = [
      item.attrs,
      ...(Array.isArray(item.marks)
        ? item.marks.map((mark) =>
            mark && typeof mark === "object" ? (mark as Record<string, unknown>).attrs : null,
          )
        : []),
    ];
    for (const attributes of attrs)
      if (attributes && typeof attributes === "object")
        for (const value of Object.values(attributes))
          if (typeof value === "string") size += value.length;
    if (size > 100000) return "Keep stories under 100,000 characters.";
    if (Array.isArray(item.content))
      for (const child of item.content) queue.push({ value: child, depth: current.depth + 1 });
  }
  return null;
}

type Attributes = NonNullable<RichTextNode["attrs"]>;
// In the order a read story lists them; anything else on a node is dropped.
const attributes: { [Key in keyof Attributes]-?: (value: unknown) => boolean } = {
  level: (value) => isWhole(value, 1, 6),
  start: (value) => isWhole(value, 1, 1000000),
  language: (value) => value === null || isText(value, 80),
  textAlign: (value) => value === null || (textAlignments as readonly unknown[]).includes(value),
  videoUrl: (value) => isText(value, 1000) && Boolean(guideVideo(value)?.embedUrl),
  mediaId: (value) => isText(value, 100) && /^[a-zA-Z0-9_-]+$/.test(value),
  alt: (value) => isText(value, 2000),
  title: (value) => value === null || isText(value, 500),
  colspan: (value) => isWhole(value, 1, 20),
  rowspan: (value) => isWhole(value, 1, 100),
  colwidth: (value) =>
    value === null ||
    (Array.isArray(value) &&
      value.length <= 20 &&
      value.every((width) => isWhole(width, 25, 2000))),
};

function readAttributes(value: unknown) {
  if (!isRecord(value)) return fail();
  const read: Record<string, unknown> = {};
  for (const [key, valid] of Object.entries(attributes)) {
    if (value[key] === undefined) continue;
    if (!valid(value[key]))
      fail(key === "videoUrl" && isText(value[key], 1000) ? videoProblem : unsupported);
    read[key] = value[key];
  }
  return read as Attributes;
}

function readMark(value: unknown): RichTextMark {
  if (!isRecord(value)) return fail();
  if (value.type === "link") {
    if (!isRecord(value.attrs)) return fail();
    const href = value.attrs.href;
    if (!isText(href, 2000) || !safeTextLink(href)) return fail(linkProblem);
    return { type: "link", attrs: { href } };
  }
  if (!(plainMarks as readonly unknown[]).includes(value.type)) return fail();
  return { type: value.type as (typeof plainMarks)[number] };
}

function readNode(value: unknown): RichTextNode {
  if (!isRecord(value) || !(nodeTypes as readonly unknown[]).includes(value.type)) return fail();
  const node: RichTextNode = { type: value.type as RichTextNode["type"] };
  if (value.text !== undefined) node.text = isText(value.text, 100000) ? value.text : fail();
  if (value.attrs !== undefined) node.attrs = readAttributes(value.attrs);
  if (value.marks !== undefined)
    node.marks =
      Array.isArray(value.marks) && value.marks.length <= 6 ? value.marks.map(readMark) : fail();
  if (value.content !== undefined)
    node.content =
      Array.isArray(value.content) && value.content.length <= 5000
        ? value.content.map(readNode)
        : fail();
  return node;
}

function validTable(table: RichTextNode) {
  const rows = table.content ?? [];
  if (!rows.length || rows.length > 100) return false;
  const occupied: boolean[][] = Array.from({ length: rows.length }, () => []);
  let width = 0;
  for (const [rowIndex, row] of rows.entries()) {
    if (row.type !== "tableRow" || (row.content?.length ?? 0) > 20) return false;
    let column = 0;
    for (const cell of row.content ?? []) {
      while (occupied[rowIndex]![column]) column++;
      const columns = cell.attrs?.colspan ?? 1,
        height = cell.attrs?.rowspan ?? 1;
      if (column + columns > 20 || rowIndex + height > rows.length) return false;
      if (cell.attrs?.colwidth && cell.attrs.colwidth.length !== columns) return false;
      for (let y = rowIndex; y < rowIndex + height; y++)
        for (let x = column; x < column + columns; x++) {
          if (occupied[y]![x]) return false;
          occupied[y]![x] = true;
        }
      column += columns;
    }
    width = Math.max(width, occupied[rowIndex]!.length);
  }
  return (
    width > 0 &&
    occupied.every(
      (row) =>
        row.length === width &&
        Array.from({ length: width }, (_, index) => row[index]).every(Boolean),
    )
  );
}

function validStructure(root: RichTextNode): boolean {
  const blocks = [
    "paragraph",
    "heading",
    "bulletList",
    "orderedList",
    "blockquote",
    "horizontalRule",
    "codeBlock",
    "image",
    "gallery",
    "videoEmbed",
    "table",
  ];
  function valid(value: RichTextNode): boolean {
    const children = value.content ?? [];
    if (value.type === "text") return !!value.text && !children.length;
    if (value.marks?.length) return false;
    if (value.type === "hardBreak" || value.type === "horizontalRule")
      return !children.length && !value.text;
    if (value.type === "videoEmbed")
      return (
        Boolean(value.attrs?.videoUrl && guideVideo(value.attrs.videoUrl)?.embedUrl) &&
        !children.length &&
        !value.text
      );
    if (value.type === "gallery")
      return (
        children.length > 0 &&
        children.length <= 30 &&
        children.every((child) => child.type === "image" && valid(child))
      );
    if (value.type === "image")
      return Boolean(value.attrs?.mediaId) && !children.length && !value.text;
    if (value.text !== undefined) return false;
    if (value.type === "heading" && ![1, 2, 3, 4, 5, 6].includes(value.attrs?.level ?? 0))
      return false;
    if (value.type === "table" && !validTable(value)) return false;
    const allowed = ["paragraph", "heading"].includes(value.type)
      ? ["text", "hardBreak"]
      : value.type === "codeBlock"
        ? ["text"]
        : ["bulletList", "orderedList"].includes(value.type)
          ? ["listItem"]
          : value.type === "table"
            ? ["tableRow"]
            : value.type === "tableRow"
              ? ["tableCell", "tableHeader"]
              : ["tableCell", "tableHeader"].includes(value.type)
                ? blocks.filter((type) => type !== "table")
                : blocks;
    if (
      ["listItem", "tableCell", "tableHeader"].includes(value.type) &&
      children[0]?.type !== "paragraph"
    )
      return false;
    return children.every((child) => allowed.includes(child.type) && valid(child));
  }
  return valid(root);
}

function collectMediaIds(root: RichTextNode): string[] {
  const ids = new Set<string>(),
    queue = [root];
  while (queue.length) {
    const value = queue.pop()!;
    if (value.type === "image" && value.attrs?.mediaId) ids.add(value.attrs.mediaId);
    queue.push(...(value.content ?? []));
  }
  return [...ids];
}

/**
 * A story as the editor and pages use it, or why it can't be one. Known fields are kept in a fixed
 * order and everything else is dropped, so two reads of the same story compare equal.
 */
export function readStory(value: unknown): { document: RichTextNode } | { problem: string } {
  const bounds = boundsProblem(value);
  if (bounds) return { problem: bounds };
  let document: RichTextNode;
  try {
    document = readNode(value);
  } catch (error) {
    if (error instanceof StoryProblem) return { problem: error.message };
    throw error;
  }
  if (document.type !== "doc") return { problem: "Expected a story document." };
  if (!validStructure(document)) return { problem: unsupported };
  if (collectMediaIds(document).length > 30) return { problem: "Use up to 30 images per story." };
  return { document };
}

/** The story, or undefined when it isn't one. */
export function readRichText(value: unknown) {
  const read = readStory(value);
  return "document" in read ? read.document : undefined;
}

/** Canonical IDs only. The creator-scoped service validates ownership and attachment. */
export function richTextMediaIds(value: unknown): string[] {
  const document = readRichText(value);
  return document ? collectMediaIds(document) : [];
}

export function plainTextDocument(text: string): RichTextNode {
  return {
    type: "doc",
    content: text
      .split("\n")
      .map((line) => ({ type: "paragraph", content: line ? [{ type: "text", text: line }] : [] })),
  };
}

export function richTextPlainText(value: RichTextNode): string {
  if (value.type === "text") return value.text ?? "";
  if (value.type === "hardBreak") return "\n";
  if (value.type === "image") return value.attrs?.alt ?? "";
  return (value.content ?? [])
    .map(richTextPlainText)
    .join(
      [
        "doc",
        "bulletList",
        "orderedList",
        "listItem",
        "blockquote",
        "table",
        "tableRow",
        "tableCell",
        "tableHeader",
      ].includes(value.type)
        ? "\n"
        : "",
    );
}
const escape = (value: string) =>
  value.replace(
    /[&<>"']/g,
    (character) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[character]!,
  );
export type RichTextMedia = { id: string; url: string; type: string; name: string; alt?: string };
function safeMediaUrl(value: string) {
  return (
    (/^\/(?!\/)/.test(value) && !/[\s<>]/.test(value)) ||
    (/^https?:\/\//i.test(value) && safeTextLink(value))
  );
}
/**
 * How a page wants a story's pictures. With `resizedImages`, a picture whose route resizes
 * (resized-images.ts) is asked for a copy instead of its original. A gallery's pictures, which
 * the page sets to its columns' width, list every step with the page's `gallerySizes`. A picture
 * on its own asks for one large step only: the page doesn't know how wide it is, and a srcset
 * step wider than the picture would draw it smaller than its own size.
 */
export type RichTextOptions = { resizedImages?: { gallerySizes: string } };

/** Only trusted tags and escaped text/URLs. Images resolve from the selected
 * authorized record's media; authored documents never provide their own URLs. */
export function renderRichText(
  value: unknown,
  media: RichTextMedia[] = [],
  options: RichTextOptions = {},
): string {
  const document = readRichText(value);
  if (!document) return "";
  function imageSource(url: string, gallery: boolean) {
    const resized = options.resizedImages;
    const srcset = resized && routeSrcset(url);
    if (!resized || !srcset) return `src="${escape(url)}"`;
    const src = `src="${escape(`${url}?w=1440`)}"`;
    return gallery
      ? `${src} srcset="${escape(srcset)}" sizes="${escape(resized.gallerySizes)}"`
      : src;
  }
  function render(value: RichTextNode, gallery = false): string {
    if (value.type === "text") {
      let text = escape(value.text ?? "");
      for (const mark of value.marks ?? []) {
        if (mark.type === "link")
          text = `<a href="${escape(mark.attrs.href)}" rel="noopener noreferrer">${text}</a>`;
        else {
          const tag = { bold: "strong", italic: "em", strike: "s", code: "code", underline: "u" }[
            mark.type
          ];
          text = `<${tag}>${text}</${tag}>`;
        }
      }
      return text;
    }
    if (value.type === "gallery")
      return `<div class="story-gallery">${(value.content ?? []).map((child) => render(child, true)).join("")}</div>`;
    if (value.type === "videoEmbed") {
      const video = guideVideo(value.attrs?.videoUrl ?? "");
      if (!video?.embedUrl) return "";
      const url = new URL(video.embedUrl);
      url.searchParams.delete("autoplay");
      return `<div class="story-video"><iframe src="${escape(url.href)}" title="${escape(value.attrs?.title || `${video.provider} video`)}" loading="lazy" allow="fullscreen; picture-in-picture" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe></div>`;
    }
    if (value.type === "image") {
      const asset = media.find(
        (asset) => asset.id === value.attrs?.mediaId && asset.type.startsWith("image/"),
      );
      if (!asset || !safeMediaUrl(asset.url)) return "";
      return `<img ${imageSource(asset.url, gallery)} alt="${escape(value.attrs?.alt ?? asset.alt ?? asset.name)}"${value.attrs?.title ? ` title="${escape(value.attrs.title)}"` : ""} loading="lazy" decoding="async">`;
    }
    const children = (value.content ?? []).map((child) => render(child)).join("");
    if (value.type === "doc") return children;
    if (value.type === "hardBreak") return "<br>";
    if (value.type === "horizontalRule") return "<hr>";
    if (value.type === "codeBlock") return `<pre><code>${children}</code></pre>`;
    if (value.type === "orderedList")
      return `<ol start="${value.attrs?.start ?? 1}">${children}</ol>`;
    if (value.type === "table")
      return `<div class="story-table-scroll" tabindex="0" role="region" aria-label="Table"><table>${children}</table></div>`;
    if (value.type === "tableRow") return `<tr>${children}</tr>`;
    if (value.type === "tableCell" || value.type === "tableHeader") {
      const tag = value.type === "tableCell" ? "td" : "th";
      return `<${tag}${tag === "th" ? ' scope="col"' : ""} colspan="${value.attrs?.colspan ?? 1}" rowspan="${value.attrs?.rowspan ?? 1}">${children}</${tag}>`;
    }
    const tag =
      value.type === "heading"
        ? `h${value.attrs?.level ?? 2}`
        : { paragraph: "p", bulletList: "ul", listItem: "li", blockquote: "blockquote" }[
            value.type
          ];
    const alignment =
      ["paragraph", "heading"].includes(value.type) && value.attrs?.textAlign
        ? ` class="story-align-${value.attrs.textAlign}"`
        : "";
    return `<${tag}${alignment}>${children}</${tag}>`;
  }
  return render(document);
}
